/**
 * @file MessagesHistoryDetails.js
 * @description Notification history details page with individual breakout counts.
 */

import { useEffect, useState, useContext } from 'react';
import '../page-list/PageList.css';
import '../../components/fileImports/ImportQueuePage.css';
import styles from '../clients/ClientList.module.scss';
import styles2 from '../population-groups/PopulationGroup.module.scss';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import Spinner from '../ui/spinners/Spinner';
import '../page-list/PageList.css';
import SearchComponent from '../helpers/search/SearchComponent';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { getNotificationMessageDetails } from '../../services/messageService';
import Toast from '../ui/toasts/Toast';
import { useParams, useNavigate } from 'react-router-dom';
import { downloadReport } from '../individual/components/NotificationHistoryDetailsReport';
import { ROUTES } from '../../constants/routes';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import SentBadge from './SentBadge';
import { Popover } from '@mui/material';
import { getText } from '../../i18n';

const STAT_ITEMS = [
    {
        key: 'reachTotal',
        label: getText('msg_0a6028b06b70'),
        icon: 'bi-people',
        color: '#1366FF',
        background: 'rgba(54, 79, 199, 0.08)',
    },
    {
        key: 'countTotal',
        label: getText('msg_a7b1ed5c5907'),
        icon: 'bi-send-fill',
        color: '#1366FF',
        background: 'rgba(54, 79, 199, 0.08)',
    },
    {
        key: 'emailTotal',
        label: getText('msg_84add5b29527'),
        icon: 'bi-envelope',
        color: '#2F2B43',
        background: 'rgba(47, 43, 67, 0.08)',
    },
    {
        key: 'smsTotal',
        label: getText('msg_c12f28e9ab88'),
        icon: 'bi-chat-text',
        color: '#14BA6D',
        background: 'rgba(20, 186, 109, 0.08)',
    },
    {
        key: 'pushTotal',
        label: getText('msg_cf73be80fa6a'),
        icon: 'bi-bell',
        color: '#667085',
        background: 'rgba(102, 112, 133, 0.08)',
    },
];

const OVERVIEW_FIELDS = [
    { key: 'name', label: getText('msg_709a23220f2c') },
    { key: 'displayName', label: getText('msg_8d6b34815ba4') },
    { key: 'id', label: getText('msg_465056ba7a6b') },
    { key: 'notificationCategory', label: getText('msg_a3c686e711e4') },
];

const BREAKOUT_ICONS = {
    email: { icon: 'bi-envelope', color: '#2F2B43' },
    sms: { icon: 'bi-chat-text', color: '#14BA6D' },
    push: { icon: 'bi-bell', color: '#667085' },
};

function StatCircle({ icon, color, background }) {
    return (
        <div
            style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                border: `1.5px solid ${color}`,
                background,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <i className={`bi ${icon}`} style={{ fontSize: '22px', color }} />
        </div>
    );
}

function StatCard({ item, value }) {
    const displayValue = value > 0 ? value.toLocaleString() : '-';

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '12px',
            }}
        >
            <StatCircle
                icon={item.icon}
                color={item.color}
                background={item.background}
            />
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <span
                    style={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#667085',
                        lineHeight: '18px',
                    }}
                >
                    {item.label}
                </span>
                <span
                    style={{
                        fontSize: '22px',
                        fontWeight: 600,
                        color: '#2F2B43',
                        lineHeight: '28px',
                    }}
                >
                    {displayValue}
                </span>
            </div>
        </div>
    );
}

function OverviewField({ label, value }) {
    return (
        <div>
            <div
                style={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#667085',
                    marginBottom: '4px',
                }}
            >
                {label}
            </div>
            <div
                style={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#2F2B43',
                }}
            >
                {value || '-'}
            </div>
        </div>
    );
}

function BreakoutItem({ icon, color, label }) {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '12px',
            }}
        >
            <i className={`bi ${icon}`} style={{ fontSize: '16px', color }} />
            <span
                style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#2F2B43',
                }}
            >
                {label}
            </span>
        </div>
    );
}

function BreakoutPopover({ anchorEl, open, onClose, record }) {
    if (!record) {
        return null;
    }

    const fullName = `${record.firstName} ${record.lastName}`;

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            slotProps={{
                paper: {
                    style: {
                        width: '378px',
                        padding: '16px 20px',
                        background: '#FFFFFF',
                        border: '1px solid #EAECF0',
                        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                        borderRadius: '10px',
                    },
                },
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '16px',
                }}
            >
                <div>
                    <div
                        style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#2F2B43',
                        }}
                    >
                        {fullName}
                    </div>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#667085',
                        }}
                    >
                        {getText('msg_959d64669a63')}
                    </div>
                </div>
                <span
                    role="button"
                    tabIndex={0}
                    onClick={onClose}
                    onKeyDown={e => e.key === 'Enter' && onClose()}
                    className="ing-text-buttons mx-3"
                >
                    <i className="bi bi-x me-2" />
                    {getText('msg_bbfa773e5a63')}
                </span>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}
            >
                {record.emails.map(email => (
                    <BreakoutItem
                        key={`email-${email}`}
                        icon={BREAKOUT_ICONS.email.icon}
                        color={BREAKOUT_ICONS.email.color}
                        label={email}
                    />
                ))}
                {record.smSs.map(sms => (
                    <BreakoutItem
                        key={`sms-${sms}`}
                        icon={BREAKOUT_ICONS.sms.icon}
                        color={BREAKOUT_ICONS.sms.color}
                        label={sms}
                    />
                ))}
                {record.pushes.map(push => (
                    <BreakoutItem
                        key={`push-${push}`}
                        icon={BREAKOUT_ICONS.push.icon}
                        color={BREAKOUT_ICONS.push.color}
                        label={push || 'Unknown'}
                    />
                ))}
            </div>
        </Popover>
    );
}

function MessagesHistoryDetails() {
    const { scheduleId } = useParams();
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const pageSize = 20;

    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [messageHistoryDetails, setMessageHistoryDetails] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [breakoutAnchorEl, setBreakoutAnchorEl] = useState(null);
    const [breakoutRecord, setBreakoutRecord] = useState(null);

    const isBreakoutOpen = Boolean(breakoutAnchorEl);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadMessageHistoryDetails();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, currentPage]);

    function handleSearch(searchBy) {
        setSearchTerm(searchBy);
    }

    async function loadMessageHistoryDetails() {
        try {
            setIsLoading(true);

            const fetchedMessageHistoryDetails =
                await getNotificationMessageDetails(
                    scheduleId,
                    searchTerm,
                    currentPage,
                    pageSize,
                );

            setMessageHistoryDetails(fetchedMessageHistoryDetails);
            setTotalPages(Math.ceil(fetchedMessageHistoryDetails.pageTotal));
        } catch (error) {
            if (error.name === 'AbortError') {
                return null;
            }
            console.error('Failed to fetch message history details', error);
            Toast.error(getText('msg_06bffa98f4a2'));
        } finally {
            setIsLoading(false);
        }
    }

    async function downloadIndividualReport() {
        try {
            setIsLoading(true);
            downloadReport(scheduleId, searchTerm);
        } catch (error) {
            console.error('Error downloading report', error);
            Toast.error(getText('msg_821613944fc1'));
        } finally {
            setIsLoading(false);
        }
    }

    function handleViewMessage() {
        showModal({
            type: ModalTypes.MESSAGE_VIEW,
            title: messageHistoryDetails.notificationSchedule.displayName,
            subtitle: getDateFromZulu(
                messageHistoryDetails.notificationSchedule.sentAt,
            ),
            text: messageHistoryDetails.htmlBody,
        });
    }

    function handleBreakoutOpen(event, record) {
        setBreakoutAnchorEl(event.currentTarget);
        setBreakoutRecord(record);
    }

    function handleBreakoutClose() {
        setBreakoutAnchorEl(null);
        setBreakoutRecord(null);
    }

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    const schedule = messageHistoryDetails.notificationSchedule || {};

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div
                        className="d-flex justify-content-between align-items-center mb-4"
                        style={{ padding: '0 24px' }}
                    >
                        <p className="page-title card-title mb-0">
                            {getText('msg_f96c2b3bc560')}
                        </p>
                        <div className="d-flex align-items-center">
                            <span
                                type="button"
                                onClick={downloadIndividualReport}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-cloud-download me-2" />
                                {getText('msg_a479c9c34e87')}
                            </span>
                            <span
                                type="button"
                                onClick={() =>
                                    navigate(ROUTES.MESSAGES_HISTORY)
                                }
                                className="ing-text-buttons mx-3"
                                tabIndex={13}
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            gap: '32px',
                            padding: '24px',
                            margin: '0 24px 24px 24px',
                            justifyContent: 'center',
                        }}
                    >
                        {STAT_ITEMS.map(item => (
                            <StatCard
                                key={item.key}
                                item={item}
                                value={messageHistoryDetails[item.key] || 0}
                            />
                        ))}
                    </div>

                    <div style={{ padding: '0 24px', marginBottom: '24px' }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                marginBottom: '16px',
                            }}
                        >
                            <span
                                style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: '#2F2B43',
                                }}
                            >
                                {getText('msg_f68cba605742')}
                            </span>
                            <span
                                style={{
                                    color: 'var(--iNGAGED-Primary-Color)',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                }}
                                onClick={handleViewMessage}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e =>
                                    e.key === 'Enter' && handleViewMessage()
                                }
                            >
                                {getText('msg_58d7a8fd7cf0')}
                            </span>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '60px',
                                flexWrap: 'wrap',
                            }}
                        >
                            {OVERVIEW_FIELDS.map(field => (
                                <OverviewField
                                    key={field.key}
                                    label={field.label}
                                    value={schedule[field.key]}
                                />
                            ))}
                            <OverviewField
                                label={getText('msg_8e6b0307ed79')}
                                value={getDateFromZulu(schedule.sentAt)}
                            />
                        </div>
                    </div>

                    <div
                        style={{
                            padding: '0 24px',
                            marginBottom: '16px',
                            paddingTop: '24px',
                        }}
                    >
                        <div className={styles.searchWrapper}>
                            <SearchComponent
                                onSearch={handleSearch}
                                placeholder={getText('msg_bce06414177f')}
                                initialValue={searchTerm}
                                autoFocus
                            />
                        </div>
                    </div>

                    <div
                        className="table-responsive-xxl w-100"
                        style={{
                            width: '100%',
                            overflowX: 'auto',
                            position: 'relative',
                            height: 'fit-content',
                        }}
                    >
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th style={{ paddingLeft: '24px' }}>
                                        <span className="table-column-header-text">
                                            {getText('msg_7aba334bb9bf')}
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_863cb39fbe7d')}
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_b6ea992aab46')}
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_35f49dcfbfb2')}
                                        </span>
                                    </th>
                                    <th style={{ textAlign: 'center' }}>
                                        <span className="table-column-header-text">
                                            {getText('msg_dc3decbb9384')}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {messageHistoryDetails.length <= 0 ? (
                                    <tr>
                                        <td colSpan={12}>
                                            <NotFoundBox
                                                backgroundColor="#f0f4ff"
                                                title={getText('msg_aca14ec90fb5')}
                                                description={getText('msg_5328029ad816')}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    ''
                                )}

                                {messageHistoryDetails.notificationIndividuals.map(
                                    (messageHistoryDetailRecord, index) => (
                                        <tr key={index}>
                                            <td style={{ paddingLeft: '24px' }}>
                                                {
                                                    messageHistoryDetailRecord.sourceId
                                                }
                                            </td>
                                            <td className="item-page-changed-by">
                                                {
                                                    messageHistoryDetailRecord.lastName
                                                }
                                            </td>
                                            <td className="item-page-changed-by">
                                                {
                                                    messageHistoryDetailRecord.firstName
                                                }
                                            </td>
                                            <td className="item-page-changed-by">
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        flexDirection: 'row',
                                                        alignItems:
                                                            'flex-start',
                                                        gap: '6px',
                                                        maxWidth: '300px',
                                                    }}
                                                >
                                                    <SentBadge
                                                        variant="email"
                                                        count={
                                                            messageHistoryDetailRecord
                                                                .emails.length
                                                        }
                                                    />
                                                    <SentBadge
                                                        variant="sms"
                                                        count={
                                                            messageHistoryDetailRecord
                                                                .smSs.length
                                                        }
                                                    />
                                                    <SentBadge
                                                        variant="push"
                                                        count={
                                                            messageHistoryDetailRecord
                                                                .pushes.length
                                                        }
                                                    />
                                                </div>
                                            </td>
                                            <td
                                                style={{ placeItems: 'center' }}
                                            >
                                                <span
                                                    className={
                                                        styles2.viewButton
                                                    }
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={e =>
                                                        handleBreakoutOpen(
                                                            e,
                                                            messageHistoryDetailRecord,
                                                        )
                                                    }
                                                    onKeyDown={e =>
                                                        e.key === 'Enter' &&
                                                        handleBreakoutOpen(
                                                            e,
                                                            messageHistoryDetailRecord,
                                                        )
                                                    }
                                                >
                                                    <i className="bi bi-eye" />
                                                </span>
                                            </td>
                                        </tr>
                                    ),
                                )}
                            </tbody>
                        </table>
                    </div>

                    <BreakoutPopover
                        anchorEl={breakoutAnchorEl}
                        open={isBreakoutOpen}
                        onClose={handleBreakoutClose}
                        record={breakoutRecord}
                    />

                    {totalPages > 0 && (
                        <div className="mt-auto">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MessagesHistoryDetails;
